import { apiFetch } from "./client";
import type { GoogleStatusResponse } from "./types";

/**
 * 3.1A — read-only Google Calendar connection status for the current user.
 * GET /Custom/oauth/google/status (verified contract, sub_0).
 *
 * NOTE: this is the ONLY Google endpoint sub_1 consumes. The mutating
 * `/oauth/google/revoke` is destructive and stays behind a soft-gate
 * (see lib/settingsConfig.ts → DISCONNECT_GOOGLE_ENABLED) until its
 * behaviour is staging-verified per TZ §6.
 */
export function fetchGoogleStatus(signal?: AbortSignal): Promise<GoogleStatusResponse> {
  return apiFetch<GoogleStatusResponse>("/Custom/oauth/google/status", { signal });
}

/**
 * Begin the Google OAuth grant (connect / reconnect / re-grant scope).
 * GET /Custom/oauth/google/initiate → `{ redirectUrl }` (verified contract).
 * Non-destructive: inserts a pending-state row and hands back Google's
 * consent URL. The caller redirects the browser to it.
 */
export function initiateGoogleOAuth(signal?: AbortSignal): Promise<{ redirectUrl: string }> {
  return apiFetch<{ redirectUrl: string }>("/Custom/oauth/google/initiate", { signal });
}

/**
 * DESTRUCTIVE — revoke the Google connection (wipes tokens, stops calendar
 * sync). POST /Custom/oauth/google/revoke. Only invoked when the
 * DISCONNECT_GOOGLE_ENABLED soft-gate is open (TZ §6 staging-verify).
 */
export function revokeGoogle(reason = "user_revoked"): Promise<{ status: string; message: string }> {
  return apiFetch<{ status: string; message: string }>("/Custom/oauth/google/revoke", {
    method: "POST",
    body: { reason },
  });
}
